import { api, ensureCsrfCookie } from '@/api/client';
import type { CursorPage } from '@/api/commercial';
import type { ExtractedField, RfqData, RfqStatus } from '@/types/generated';

/**
 * Requests for quotation.
 *
 * The response types are GENERATED from the PHP DTOs, so this client cannot drift
 * from the wire. Same cursor-paginated shape as customers and the catalogue — see
 * `commercial.ts` for why offset paging is avoided.
 */

export interface RfqFilters {
  search?: string | undefined;
  status?: string | undefined;
  customer_id?: string | undefined;
  openOnly?: boolean | undefined;
  cursor?: string | null | undefined;
}

export async function fetchRfqs(filters: RfqFilters = {}): Promise<CursorPage<RfqData>> {
  const { data } = await api.get<CursorPage<RfqData>>('/rfqs', {
    params: pruned({
      search: filters.search,
      status: filters.status,
      customer_id: filters.customer_id,
      open_only: filters.openOnly ? 1 : undefined,
      cursor: filters.cursor ?? undefined,
    }),
  });
  return data;
}

export async function fetchRfq(id: string): Promise<RfqData> {
  const { data } = await api.get<{ rfq: RfqData }>(`/rfqs/${id}`);
  return data.rfq;
}

/** The shape the wizard submits. Mirrors StoreRfqRequest. */
export interface CreateRfqPayload {
  customer_id: string;
  origin_description: string;
  origin_city?: string | undefined;
  /**
   * ISO 3166-1 alpha-2. The server has always accepted these; nothing sent them until now, so a
   * pricing rule scoped to a country pair matched nothing — and the extractor's country output was
   * discarded on the way into the form.
   */
  origin_country?: string | undefined;
  destination_description: string;
  destination_city?: string | undefined;
  destination_country?: string | undefined;
  service_category: string;
  cargo_type: string;
  incoterm?: string | undefined;
  weight_kg: number;
  volume_m3?: number | undefined;
  piece_count?: number | undefined;
  currency: string;
  declared_value_minor?: number | undefined;
  distance_km?: number | undefined;
  temperature_controlled?: boolean | undefined;
  hazardous?: boolean | undefined;
  oversized?: boolean | undefined;
  high_value?: boolean | undefined;
  un_number?: string | undefined;
  ready_date?: string | undefined;
  required_delivery_date?: string | undefined;
  respond_by?: string | undefined;
  notes?: string | undefined;
  line_items: {
    description: string;
    quantity: number;
    unit_of_measure: string;
    product_id?: string | undefined;
  }[];
}

export async function createRfq(payload: CreateRfqPayload): Promise<RfqData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ rfq: RfqData }>('/rfqs', payload);
  return data.rfq;
}

/**
 * What the parser made of a pasted request.
 *
 * `values` is the merge payload for the form; `fields` is the reviewable record behind it,
 * including the fields that were missed. The two cannot disagree — the server derives the
 * first from the second — so the panel shows `fields` and the form applies `values`.
 */
export interface ExtractionResponse {
  fields: ExtractedField[];
  values: Record<string, string | number | boolean>;
  needs_review: ExtractedField[];
  not_found: ExtractedField[];
  summary: string;
  source_length: number;
  /** Always `deterministic_parser`. Rendered in the panel so the UI cannot overstate it. */
  method: string;
}

/**
 * Read an RFQ out of pasted text. Writes nothing — paste as many times as you like.
 */
export async function extractRfq(text: string): Promise<ExtractionResponse> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ data: ExtractionResponse }>('/rfqs/extract', { text });
  return data.data;
}

export async function submitRfq(id: string): Promise<RfqData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ rfq: RfqData }>(`/rfqs/${id}/submit`);
  return data.rfq;
}

/**
 * Any other lifecycle move. `reason` is required by the server when the target is
 * `lost` — a loss with no reason is a data point nobody can learn from.
 */
export async function transitionRfq(
  id: string,
  status: RfqStatus,
  reason?: string,
): Promise<RfqData> {
  await ensureCsrfCookie();
  const { data } = await api.post<{ rfq: RfqData }>(`/rfqs/${id}/transition`, { status, reason });
  return data.rfq;
}

function pruned(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '' && value !== null),
  );
}
